/**
 * JournifiSync Service
 * 
 * Imports trades from NinjaTrader via JournifiSync AddOn
 * Reads JSON execution files and matches them into complete trades
 * 
 * Author: AlphaWolfTrader
 * Version: 1.0.0
 */

import {
  groupExecutionsIntoTrades,
  buildJournalTrade,
  buildExecutionIdIndex,
  classifyBuiltTrade,
  buildTradeSignature
} from './tradeProcessor';

// Default sync folder path - ORIGINAL WORKING PATH
const DEFAULT_SYNC_FOLDER = 'C:\\NinjaTrader8Data\\JournifiSync';

/**
 * Check if running in Electron environment
 */
const isElectron = () => {
  return typeof window !== 'undefined' && window.electronAPI?.isElectron;
};

/**
 * Read JSON file using Electron IPC
 */
const readJsonFile = async (filePath) => {
  if (!isElectron()) {
    throw new Error('JournifiSync import requires Electron environment');
  }
  
  const result = await window.electronAPI.journifiSync.readFile(filePath);
  if (!result.success) {
    throw new Error(result.error);
  }
  return result.data;
};

/**
 * List JSON files in a directory using Electron IPC
 */
const listJsonFiles = async (folderPath) => {
  if (!isElectron()) {
    throw new Error('JournifiSync import requires Electron environment');
  }
  
  const result = await window.electronAPI.journifiSync.listFiles(folderPath);
  if (!result.success) {
    console.error('Error listing JournifiSync files:', result.error);
    return [];
  }
  
  return result.files.map(f => f.path).sort();
};

/**
 * Check if sync folder exists and has files
 */
export const checkSyncFolder = async (folderPath = DEFAULT_SYNC_FOLDER) => {
  if (!isElectron()) {
    return { exists: false, error: 'Not in Electron environment' };
  }
  
  return await window.electronAPI.journifiSync.checkFolder(folderPath);
};

/**
 * Get list of unique account names from sync folder
 * Scans all execution files and extracts unique AccountName values
 */
export const getAvailableAccounts = async (folderPath = DEFAULT_SYNC_FOLDER) => {
  if (!isElectron()) {
    return { success: false, accounts: [], error: 'Not in Electron environment' };
  }
  
  try {
    const files = await listJsonFiles(folderPath);
    const accountSet = new Set();
    
    for (const file of files) {
      try {
        const executions = await readJsonFile(file);
        if (Array.isArray(executions)) {
          executions.forEach(exec => {
            if (exec.AccountName) {
              accountSet.add(exec.AccountName);
            }
          });
        }
      } catch (error) {
        console.warn(`Error reading ${file} for accounts:`, error.message);
      }
    }
    
    return { 
      success: true, 
      accounts: Array.from(accountSet).sort() 
    };
  } catch (error) {
    return { success: false, accounts: [], error: error.message };
  }
};

// Trade processing functions (parseAtmTemplateName, groupExecutionsIntoTrades, buildJournalTrade)
// are imported from './tradeProcessor.js' — single source of truth.

/**
 * Import trades from JournifiSync folder
 * 
 * @param {Object} options - Import options
 * @param {string} options.folderPath - Path to JournifiSync folder
 * @param {string} options.startDate - Only import trades on or after this date (YYYY-MM-DD)
 * @param {string} options.endDate - Only import trades on or before this date (YYYY-MM-DD)
 * @param {Array} options.selectedAccounts - Only import trades from these accounts (null = all)
 * @param {Array} options.existingAccounts - Existing accounts for matching
 * @param {Array} options.existingTrades - Existing trades to check for duplicates
 * @returns {Promise<Object>} - { trades: [], errors: [], stats: {} }
 */
export const importFromJournifiSync = async (options = {}) => {
  const {
    folderPath = DEFAULT_SYNC_FOLDER,
    startDate = null,
    endDate = null,
    selectedAccounts = null, // null means all accounts
    accountMappings = {}, // NT account name -> Journifi account ID
    existingAccounts = [],
    existingTrades = []
  } = options;
  
  const result = {
    trades: [],
    errors: [],
    stats: {
      filesProcessed: 0,
      executionsFound: 0,
      executionDuplicatesRemoved: 0,
      tradesCreated: 0,
      duplicatesSkipped: 0,
      duplicatesByExecId: 0,      // stored trade has same exec IDs + same accountId
      duplicatesBySignature: 0,   // signature collision (cross-account copy-trade or same account re-import)
      duplicatesInBatch: 0,       // within this import batch
      repairCandidates: 0,        // stored trade matches by exec IDs but its accountId is empty/different
      extensionCandidates: 0,     // partial exec ID overlap — new fills for an open position
      openPositions: 0
    },
    newAccounts: [], // Accounts that need to be created
    repairs: []      // Ghost trades that need accountId repair: {tradeId, currentAccountId, proposedAccountId, tradeSummary}
  };
  
  try {
    // Get list of JSON files
    const files = await listJsonFiles(folderPath);
    
    if (files.length === 0) {
      result.errors.push('No execution files found in sync folder');
      return result;
    }
    
    // Filter files by date range if specified
    const filteredFiles = files.filter(f => {
      const match = f.match(/executions_(\d{4}-\d{2}-\d{2})\.json$/);
      if (!match) return false;
      const fileDate = match[1];
      if (startDate && fileDate < startDate) return false;
      if (endDate && fileDate > endDate) return false;
      return true;
    });
    
    // Collect all executions
    const allExecutions = [];
    
    for (const file of filteredFiles) {
      try {
        const executions = await readJsonFile(file);
        if (Array.isArray(executions)) {
          allExecutions.push(...executions);
          result.stats.filesProcessed++;
        }
      } catch (error) {
        result.errors.push(`Error reading ${file}: ${error.message}`);
      }
    }
    
    result.stats.executionsFound = allExecutions.length;
    
    if (allExecutions.length === 0) {
      result.errors.push('No executions found in date range');
      return result;
    }
    
    // DEDUPLICATE executions by ExecutionId (same execution may appear in multiple files)
    const executionMap = new Map();
    allExecutions.forEach(exec => {
      if (exec.ExecutionId && !executionMap.has(exec.ExecutionId)) {
        executionMap.set(exec.ExecutionId, exec);
      }
    });
    const uniqueExecutions = Array.from(executionMap.values());
    result.stats.executionDuplicatesRemoved = allExecutions.length - uniqueExecutions.length;
    
    if (result.stats.executionDuplicatesRemoved > 0) {
      console.log(`[JournifiSync] Removed ${result.stats.executionDuplicatesRemoved} duplicate executions`);
    }
    
    // Filter by selected accounts if specified
    const filteredExecutions = selectedAccounts && selectedAccounts.length > 0
      ? uniqueExecutions.filter(exec => selectedAccounts.includes(exec.AccountName))
      : uniqueExecutions;
    
    if (filteredExecutions.length === 0) {
      result.errors.push('No executions found for selected accounts');
      return result;
    }
    
    // Group executions into trades
    const matchedTrades = groupExecutionsIntoTrades(filteredExecutions);

    // Account-aware execution-ID index (exec ID → {tradeId, accountId, isOpen, date}).
    // Lets us distinguish true duplicates from repair candidates (ghost trades where
    // a previous import saved with wrong/empty accountId).
    const execIdIndex = buildExecutionIdIndex(existingTrades);

    // Account-aware signature index for fallback dedup when exec IDs aren't present
    // (e.g. legacy imports that pre-date the _journifiSync metadata).
    const existingSignaturesByAccount = new Map(); // accountId → Set<signature>
    existingTrades.forEach(trade => {
      const sig = buildTradeSignature(trade);
      if (!sig) return;
      const bucket = existingSignaturesByAccount.get(trade.accountId || '') || new Set();
      bucket.add(sig);
      existingSignaturesByAccount.set(trade.accountId || '', bucket);
    });

    // Track accounts that need to be created
    const accountsToCreate = new Set();

    // Track trade signatures for this import batch (prevent duplicates within same import)
    const batchTradeSignatures = new Set();
    // Track which existing trade IDs are already being consumed as repair/extension
    // targets so we don't double-map two different preview groups to the same journal trade.
    const consumedTradeIds = new Set();

    // Convert to Journifi trades
    for (const matchedTrade of matchedTrades) {
      const journifiTrade = buildJournalTrade(matchedTrade, { existingAccounts, accountMappings, source: 'import' });
      if (!journifiTrade) continue;

      // ─── Classify against the existing journal by execution-ID overlap ───
      const classification = classifyBuiltTrade(journifiTrade, execIdIndex);

      if (classification.kind === 'duplicate') {
        // Same exec IDs + same accountId → truly already imported
        result.stats.duplicatesSkipped++;
        result.stats.duplicatesByExecId++;
        continue;
      }

      if (classification.kind === 'repair' && !consumedTradeIds.has(classification.matchedTradeId)) {
        // Existing trade has these exec IDs but its accountId is empty or different.
        // Emit a repair candidate instead of silently hiding the preview trade.
        consumedTradeIds.add(classification.matchedTradeId);
        result.stats.repairCandidates++;
        result.repairs.push({
          tradeId: classification.matchedTradeId,
          currentAccountId: classification.currentAccountId || '',
          proposedAccountId: classification.proposedAccountId || '',
          ntAccountName: matchedTrade.accountName,
          tradeSummary: {
            date: journifiTrade.date,
            symbol: journifiTrade.symbol,
            direction: journifiTrade.direction,
            quantity: journifiTrade.quantity,
            entry: journifiTrade.entry,
            pnl: journifiTrade.pnl,
            isOpen: journifiTrade.isOpen
          }
        });
        // Don't add to result.trades — repair will update the existing record in place.
        continue;
      }

      if (classification.kind === 'extension' && !consumedTradeIds.has(classification.matchedTradeId)) {
        // Partial exec-ID overlap → new fills arrived for an already-journaled position.
        // For manual import this is rare (user usually imports stale data, not live updates),
        // but surface it so we don't silently swallow new fills.
        consumedTradeIds.add(classification.matchedTradeId);
        result.stats.extensionCandidates++;
        // Fall through and let the user import the updated trade; the dedup at apply
        // time will merge it with the existing record by id.
        journifiTrade._extensionOf = classification.matchedTradeId;
      }

      // Content-based signature fallback (scoped to the trade's own account bucket)
      const tradeSignature = buildTradeSignature(journifiTrade);
      const sameAccountSigs = existingSignaturesByAccount.get(journifiTrade.accountId || '');
      if (sameAccountSigs?.has(tradeSignature)) {
        // Same signature + same account → legacy duplicate (no _journifiSync metadata).
        // NOT a copy-trade collision because we scoped by accountId.
        result.stats.duplicatesSkipped++;
        result.stats.duplicatesBySignature++;
        console.log(`[JournifiSync] Skipping legacy duplicate (same account): ${tradeSignature}`);
        continue;
      }

      // Check for duplicate within this import batch
      if (batchTradeSignatures.has(tradeSignature)) {
        result.stats.duplicatesSkipped++;
        result.stats.duplicatesInBatch++;
        console.log(`[JournifiSync] Skipping duplicate trade (within batch): ${tradeSignature}`);
        continue;
      }

      batchTradeSignatures.add(tradeSignature);

      // Track if account needs to be created
      if (!journifiTrade.accountId) {
        accountsToCreate.add(matchedTrade.accountName);
      }

      if (journifiTrade.isOpen) {
        result.stats.openPositions++;
      }

      result.trades.push(journifiTrade);
      result.stats.tradesCreated++;
    }
    
    // Create account suggestions
    result.newAccounts = Array.from(accountsToCreate).map(name => ({
      suggestedName: name,
      // Try to detect broker from connection name
      suggestedBroker: allExecutions.find(e => e.AccountName === name)?.ConnectionName || 'Unknown'
    }));
    
  } catch (error) {
    result.errors.push(`Import error: ${error.message}`);
  }
  
  return result;
};

/**
 * Get import preview without actually importing
 */
export const previewJournifiSyncImport = async (options = {}) => {
  const result = await importFromJournifiSync({
    ...options,
    existingTrades: options.existingTrades || []
  });
  
  return {
    ...result,
    // Don't return actual trades for preview, just summaries
    tradeSummaries: result.trades.map(t => ({
      date: t.date,
      symbol: t.symbol,
      direction: t.direction,
      quantity: t.quantity,
      entry: t.entry,
      pnl: t.pnl,
      atmStrategy: t.atmStrategy,
      accountId: t.accountId,
      isOpen: t.isOpen
    })),
    trades: [] // Clear trades from preview
  };
};

const journifiSyncService = {
  checkSyncFolder,
  getAvailableAccounts,
  importFromJournifiSync,
  previewJournifiSyncImport
};

export default journifiSyncService;
